import { useEffect, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CalendarCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "../components/ui/date-picker";

const STATUS = {
  present: { label: "Presente", color: "bg-emerald-100 text-emerald-700" },
  absent: { label: "Assente", color: "bg-red-100 text-red-700" },
  late: { label: "Ritardo", color: "bg-amber-100 text-amber-700" },
};

export default function AttendancePage() {
  const { activeTeam, season } = useTeams();
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [eventType, setEventType] = useState("training");
  const [date, setDate] = useState("");
  const [records, setRecords] = useState({}); // {player_id: status}
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    if (!activeTeam) return;
    const [a, p] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/attendance`, { params: { season } }),
      api.get(`/teams/${activeTeam.id}/players`),
    ]);
    setItems(a.data);
    setPlayers(p.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeam, season]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const newSession = () => {
    setEditingId(null);
    setEventType("training");
    setDate(new Date().toISOString().slice(0, 10));
    const initial = {};
    players.forEach((p) => (initial[p.id] = "present"));
    setRecords(initial);
    setOpen(true);
  };

  const editSession = (a) => {
    setEditingId(a.id);
    setEventType(a.event_type);
    setDate(a.date);
    const map = {};
    a.records.forEach((r) => (map[r.player_id] = r.status));
    setRecords(map);
    setOpen(true);
  };

  const submit = async () => {
    if (!date) return toast.error("Data richiesta");
    const payload = {
      team_id: activeTeam.id,
      season,
      event_type: eventType,
      date,
      records: players.map((p) => ({
        player_id: p.id,
        status: records[p.id] || "absent",
      })),
    };
    if (editingId) await api.put(`/attendance/${editingId}`, payload);
    else await api.post(`/attendance`, payload);
    toast.success("Presenze salvate");
    setOpen(false);
    load();
  };

  const remove = async (a) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/attendance/${a.id}`);
    load();
  };

  const summarize = (a) => {
    const counts = { present: 0, absent: 0, late: 0 };
    a.records.forEach((r) => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Presenze</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Registro presenze
          </h1>
        </div>
        <Button
          onClick={newSession}
          className="rounded-full bg-sky-500 hover:bg-sky-600"
          data-testid="new-attendance-btn"
        >
          <Plus size={16} className="mr-1" /> Nuova sessione
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((a) => {
          const c = summarize(a);
          return (
            <Card
              key={a.id}
              className="p-5 border-slate-200 flex items-center gap-4 flex-wrap cursor-pointer hover:shadow-md transition"
              onClick={() => editSession(a)}
              data-testid={`attendance-${a.id}`}
            >
              <div className="h-12 w-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <CalendarCheck size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold">
                  {a.event_type === "training" ? "Allenamento" : "Partita"} · {a.date}
                </div>
                <div className="text-sm text-slate-500 mt-1 flex gap-3">
                  <span>✅ {c.present || 0} presenti</span>
                  <span>❌ {c.absent || 0} assenti</span>
                  <span>⏰ {c.late || 0} in ritardo</span>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(a);
                }}
              >
                <Trash2 size={15} className="text-red-500" />
              </Button>
            </Card>
          );
        })}
        {items.length === 0 && (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            Nessuna sessione di presenze.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sessione presenze</DialogTitle>
          <DialogDescription className="sr-only">Registra le presenze per la seduta selezionata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo evento</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="training">Allenamento</SelectItem>
                    <SelectItem value="match">Partita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  data-testid="attendance-date"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Giocatori ({players.length})</Label>
              <div className="border border-slate-200 rounded-xl max-h-80 overflow-y-auto">
                {players.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 p-2.5 border-b border-slate-100"
                  >
                    <span className="h-6 min-w-6 px-1 rounded bg-slate-100 text-xs font-bold flex items-center justify-center">
                      {p.jersey_number ?? "—"}
                    </span>
                    <span className="flex-1 text-sm">
                      {p.last_name} {p.first_name}
                    </span>
                    <div className="flex gap-1">
                      {Object.entries(STATUS).map(([key, { label, color }]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setRecords({ ...records, [p.id]: key })}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                            records[p.id] === key
                              ? color + " ring-2 ring-offset-1 ring-current"
                              : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                          }`}
                          data-testid={`attend-${p.id}-${key}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-attendance-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
